import React, { Fragment, useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Dialog, DialogContent, DialogActions, Box } from '@mui/material';

import { Typography, colors, Dropdown, IconGraphy, Button } from '@leapeasy/ui-kit';
import ClaimDatePickerInput from 'components/ClaimForm/ClaimDatePickerInput';

import {
  setDataAction,
  getLandlordAction,
  getApartmentAction,
  getRiderAction,
  getTenantsAction,
  setLandlordArrayAction,
  setApartmentArrayAction,
  setRiderArrayAction,
  checkPolicyDuplicatedAction,
  setPolicyDuplicatedAction,
} from 'store/actions/claimFormActions';
import { UsStates } from 'utils/constants';
import { isPolicyNumberEntryEnabled } from 'utils/policyEntryMode';
import { getLandlordDisplayName, formatIsoDateForPicker } from 'components/ClaimForm/helpers';
import { selectClaimFormState } from 'utils/claimFormState';
import FileSearchIcon from 'components/ClaimForm/FileSearchIcon';
import Form1PolicyLookup from './Form_1PolicyLookup';
import {
  cancellationDisclaimerFieldsFromPolicy,
  clearedCancellationDisclaimerFields,
} from 'utils/cancellationDisclaimer';

const Form1Dropdown = (props) => {
  const { actions, token, handleNext, handleBack, isPortalPrefill = false } = props;
  const formData = useSelector(selectClaimFormState);
  const [state, setState] = useState(null);
  const [landlord, setLandlord] = useState(null);
  const [apartment, setApartment] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [landlordArray, setLandlordArray] = useState(null);
  const [apartmentArray, setApartmentArray] = useState(null);
  const [policyArray, setRiderArray] = useState(null);
  const [stateArray, setStateArray] = useState(null);
  const [duplicated, setDuplicated] = useState(null);
  const [riderName, setRiderName] = useState(null);
  const [policyStartDate, setPolicyStartDate] = useState(null);
  const [policyEndDate, setPolicyEndDate] = useState(null);
  const [futurePolicy, setFuturePolicy] = useState(false);
  const [leaseStartDate, setLeaseStartDate] = useState(null);
  const [leaseEndDate, setLeaseEndDate] = useState(null);
  const [leaseDateError, setLeaseDateError] = useState(false);
  const [isCorrectTerm, setIsCorrectTerm] = useState(null);
  const [isValidPolicy, setIsValidPolicy] = useState(0);

  const validateLeaseDatesAgainstPolicy = (startDate, endDate) => {
    if (!startDate || !endDate || !policyStartDate || !policyEndDate) {
      return false;
    }

    const startMismatch =
      new Date(startDate).getMonth() !== new Date(policyStartDate).getMonth() ||
      new Date(startDate).getFullYear() !== new Date(policyStartDate).getFullYear();
    const endMismatch =
      new Date(endDate).getMonth() !== new Date(policyEndDate).getMonth() ||
      new Date(endDate).getFullYear() !== new Date(policyEndDate).getFullYear();

    return startMismatch || endMismatch;
  };

  useEffect(() => {
    if (!formData?.data) {
      return;
    }
    setState(formData.data.state);
    setLandlord(formData.data.landlord);
    setApartment(formData.data.apartment);
    setPolicy(formData.data.policy);
    if (formData.data.riderName) {
      setRiderName(formData.data.riderName);
    }
    setStateArray(UsStates);
    setLandlordArray(formData.landlordArray);
    setApartmentArray(formData.apartmentArray);
    setRiderArray(formData.policyArray);
    setLeaseStartDate(formatIsoDateForPicker(formData.data.leaseStartDate));
    setLeaseEndDate(formatIsoDateForPicker(formData.data.leaseEndDate));
    setIsCorrectTerm(formData.data.isCorrectTerm);
    setPolicyStartDate(formatIsoDateForPicker(formData.data.policyStartDate));
    setPolicyEndDate(formatIsoDateForPicker(formData.data.policyEndDate));
    if (formData.policyDuplicated) {
      formData.policyDuplicated.length > 0
        ? setDuplicated(formData.policyDuplicated[0])
        : setDuplicated(null);
    } else {
      setDuplicated(null);
    }
  }, [formData]);

  useEffect(() => {
    if (!formData?.data?.policy || !policyArray?.length) {
      return;
    }
    const selected = policyArray.find((item) => item.Id === formData.data.policy);
    if (!selected) {
      return;
    }
    const nextShow = Boolean(selected.showCancellationDisclaimer);
    if (formData.data.showCancellationDisclaimer === nextShow) {
      return;
    }
    actions.setDataAction({
      ...formData.data,
      ...cancellationDisclaimerFieldsFromPolicy(selected),
    });
  }, [policyArray, formData?.data?.policy]);

  useEffect(() => {
    if (!policy) {
      return;
    }

    // Portal row already passed Policies-table eligibility; wait for rider list before validating.
    if (!policyArray) {
      if (isPortalPrefill) {
        setIsValidPolicy(0);
      }
      return;
    }

    const selectedPolicy = policyArray.find((row) => row.Id === policy);
    if (!selectedPolicy) {
      setIsValidPolicy(isPortalPrefill ? 0 : 1);
      return;
    }

    if (selectedPolicy.CoverageDurationInMonths__c === 0) {
      setIsValidPolicy(2);
      return;
    }

    const daysSinceLeaseEnd =
      (Date.now() - new Date(selectedPolicy.Lease_End_Date__c)) / 3600000 / 24;
    setIsValidPolicy(daysSinceLeaseEnd >= 120 ? 3 : 0);
  }, [policy, policyArray, isPortalPrefill]);

  const buildPolicyOptions = () => {
    const options = (policyArray || []).map((policy) => ({
      text: policy.Renewal_Rider_ID__c,
      value: policy.Id,
    }));

    if (
      isPortalPrefill &&
      policy &&
      formData?.data?.riderName &&
      !options.some((option) => option.value === policy)
    ) {
      options.unshift({ text: formData.data.riderName, value: policy });
    }

    return options;
  };

  const buildLandlordOptions = () => {
    const options = (landlordArray || []).map((landlord) => ({
      text: getLandlordDisplayName(landlord),
      value: landlord.Landlord__c,
    }));

    if (
      isPortalPrefill &&
      landlord &&
      formData?.data?.landlordName &&
      !options.some((option) => option.value === landlord)
    ) {
      options.unshift({ text: formData.data.landlordName, value: landlord });
    }

    return options;
  };

  const buildApartmentOptions = () => {
    const options = (apartmentArray || []).map((apartment) => ({
      text: apartment.Name,
      value: apartment.Id,
    }));

    if (
      isPortalPrefill &&
      apartment &&
      formData?.data?.propertyName &&
      !options.some((option) => option.value === apartment)
    ) {
      options.unshift({ text: formData.data.propertyName, value: apartment });
    }

    return options;
  };

  const handleState = (event) => {
    setState(event.target.value);
    actions.setDataAction({
      state: event.target.value,
      landlord: null,
      apartment: null,
      policy: null,
      ...clearedCancellationDisclaimerFields(),
    });
    actions.setLandlordArrayAction(null);
    actions.setApartmentArrayAction(null);
    actions.setRiderArrayAction(null);
    actions.setPolicyDuplicatedAction(null);
    actions.getLandlordAction({
      token,
      state: event.target.value,
    });
  };

  const handleLandlord = (event) => {
    setLandlord(event.target.value);
    actions.getApartmentAction({
      token,
      landlordId: event.target.value,
      state,
    });
    actions.setApartmentArrayAction(null);
    actions.setRiderArrayAction(null);
    actions.setPolicyDuplicatedAction(null);
    actions.setDataAction({
      ...formData.data,
      landlord: event.target.value,
      apartment: null,
      policy: null,
      ...clearedCancellationDisclaimerFields(),
    });
  };

  const handleApartment = (event) => {
    setApartment(event.target.value);
    actions.getRiderAction({
      token,
      propertyId: event.target.value,
    });
    actions.setRiderArrayAction(null);
    actions.setPolicyDuplicatedAction(null);
    actions.setDataAction({
      ...formData.data,
      apartment: event.target.value,
      policy: null,
      ...clearedCancellationDisclaimerFields(),
    });
  };

  const handlePolicy = (event) => {
    const policyId = event.target.value;
    const policy = policyArray.filter((item) => item.Id === policyId);
    setRiderName(policy[0].Renewal_Rider_ID__c);
    let policy_start_date, policy_end_date;
    if (policy[0].CoverageStartDate__c) {
      policy_start_date = policy[0].CoverageStartDate__c;
    } else {
      policy_start_date = policy[0].Lease_Start_Date__c;
    }
    if (policy[0].CoverageEndDate__c) {
      policy_end_date = policy[0].CoverageEndDate__c;
    } else {
      policy_end_date = policy[0].Lease_End_Date__c;
    }
    if (new Date(policy[0].CoverageStartDate__c) > new Date()) {
      setFuturePolicy(true);
    }
    setPolicyStartDate(
      policy_start_date
        ? `${policy_start_date.split('-')[1]}/${policy_start_date.split('-')[2]}/${policy_start_date.split('-')[0]}`
        : '',
    );
    setPolicyEndDate(
      policy_end_date
        ? `${policy_end_date.split('-')[1]}/${policy_end_date.split('-')[2]}/${policy_end_date.split('-')[0]}`
        : '',
    );
    setPolicy(policyId);
    actions.checkPolicyDuplicatedAction({ riderId: policyId });
    actions.getTenantsAction(policyId);
    actions.setDataAction({
      ...formData.data,
      policy: policyId,
      chosenTenant: null,
      riderName: policy[0].Renewal_Rider_ID__c,
      policyStartDate: policy_start_date,
      policyEndDate: policy_end_date,
      ...cancellationDisclaimerFieldsFromPolicy(policy[0]),
    });
  };

  const handleLeaseDate = (e, label) => {
    const value = e.target.value;
    if (label === 'lease_start_date') {
      setLeaseStartDate(value);
    } else {
      setLeaseEndDate(value);
    }
    // User is editing — dismiss any open modal and require re-confirmation on Next.
    setLeaseDateError(false);
    setIsCorrectTerm(null);
  };

  const handleNextButton = () => {
    actions.setDataAction({
      ...formData.data,
      isCorrectTerm,
      leaseStartDate,
      leaseEndDate,
    });

    if (validateLeaseDatesAgainstPolicy(leaseStartDate, leaseEndDate) && isCorrectTerm !== 'Yes') {
      setLeaseDateError(true);
      return;
    }

    handleNext();
  };

  const handleLeaseTermConfirm = (confirmed) => {
    setIsCorrectTerm(confirmed ? 'Yes' : 'No');
    setLeaseDateError(false);

    if (confirmed) {
      actions.setDataAction({
        ...formData.data,
        isCorrectTerm: 'Yes',
        leaseStartDate,
        leaseEndDate,
      });
      handleNext();
    }
  };

  return (
    <Fragment>
      <Box display="flex" flexDirection="column" gap="4px">
        <Dropdown
          value={state}
          size="medium"
          onChange={(e) => handleState(e)}
          disabled={isPortalPrefill}
          options={(stateArray || []).map((state) => ({
            text: state.abbreviation,
            value: state.abbreviation,
          }))}
          multiselect={false}
          placeholder="State"
          label="State"
          icon={<IconGraphy icon="MapTravel.Map" />}
          iconVisible
          style={{
            minWidth: '100%',
          }}
        />

        <Dropdown
          value={landlord}
          size="medium"
          onChange={(e) => handleLandlord(e)}
          disabled={isPortalPrefill || !landlordArray}
          options={buildLandlordOptions()}
          multiselect={false}
          placeholder="Operator"
          label="Operator"
          showSearch={true}
          icon={<IconGraphy icon="HomePlaces.Business" />}
          iconVisible
          style={{
            minWidth: '100%',
          }}
        />

        <Dropdown
          value={apartment}
          size="medium"
          onChange={(e) => handleApartment(e)}
          disabled={isPortalPrefill || !apartmentArray}
          options={buildApartmentOptions()}
          multiselect={false}
          placeholder="Property Name"
          label="Property Name"
          showSearch={true}
          icon={<IconGraphy icon="HomePlaces.Apartment" />}
          iconVisible
          style={{
            minWidth: '100%',
          }}
        />

        <Dropdown
          value={policy}
          size="medium"
          onChange={(e) => handlePolicy(e)}
          disabled={isPortalPrefill || !policyArray || !policyArray.length}
          options={buildPolicyOptions()}
          multiselect={false}
          placeholder="Select item"
          label="Policy Number"
          showSearch={true}
          icon={<IconGraphy icon="EditorLayout.Numbers" />}
          iconVisible
          style={{
            minWidth: '100%',
          }}
        />

        <Box display="flex" gap={2}>
          <Box sx={{ flexGrow: 1, width: 0 }}>
            <ClaimDatePickerInput
              label="Policy Start Date"
              value={policyStartDate || ''}
              minDate="01/01/2010"
              size="medium"
              showActionButtons={false}
              maxWidth="100%"
              disabled
            />
          </Box>

          <Box sx={{ flexGrow: 1, width: 0 }}>
            <ClaimDatePickerInput
              label="Policy End Date"
              value={policyEndDate || ''}
              size="medium"
              minDate="01/01/2010"
              showActionButtons={false}
              maxWidth="100%"
              disabled
            />
          </Box>
        </Box>

        <Box display="flex" gap={2}>
          <Box sx={{ flexGrow: 1, width: 0, position: 'relative' }}>
            <ClaimDatePickerInput
              label="Lease Start Date"
              value={leaseStartDate || ''}
              minDate="01/01/2018"
              maxWidth="100%"
              size="medium"
              showActionButtons={false}
              disabled={!(policy || formData?.data?.policy)}
              onChange={(e) => handleLeaseDate(e, 'lease_start_date')}
            />
          </Box>

          <Box sx={{ flexGrow: 1, width: 0, position: 'relative' }}>
            <ClaimDatePickerInput
              label="Lease End Date"
              value={leaseEndDate || ''}
              minWidth="100%"
              minDate="01/01/2018"
              size="medium"
              showActionButtons={false}
              disabled={!(policy || formData?.data?.policy)}
              maxWidth="100%"
              onChange={(e) => handleLeaseDate(e, 'lease_end_date')}
            />
          </Box>
        </Box>

        {formData &&
          formData.policyDuplicated &&
          formData.policyDuplicated.length === 0 &&
          futurePolicy && (
            <Dialog open={futurePolicy} maxWidth="md">
              <DialogContent>
                <Typography
                  variant="body1"
                  style={{ fontWeight: '600', textAlign: 'center', color: colors.purple[900] }}
                >
                  The policy period you have selected is a future policy that has not started yet.
                </Typography>
                <Typography
                  variant="body2"
                  style={{ textAlign: 'center', color: colors.neutral[900], marginTop: '8px' }}
                >
                  Please select a current policy that matches the current lease dates.
                </Typography>
                <Typography
                  variant="body2"
                  style={{ textAlign: 'center', color: colors.neutral[900], marginTop: '8px' }}
                >
                  If you have any questions, please contact{' '}
                  <a href={'mailto:claims@leapeasy.com'}>claims@leapeasy.com</a>
                </Typography>
              </DialogContent>

              <DialogActions>
                <Button variant="primary" onClick={() => setFuturePolicy(false)}>
                  Ok
                </Button>
              </DialogActions>
            </Dialog>
          )}

        {isValidPolicy === 1 && (
          <Dialog open={isValidPolicy === 1} maxWidth="md">
            <DialogContent>
              <Typography
                variant="body1"
                style={{ fontWeight: '600', textAlign: 'center', color: colors.purple[900] }}
              >
                The property doesn't have this policy in SF
              </Typography>
              <Typography
                variant="body2"
                style={{ textAlign: 'center', color: colors.neutral[900], marginTop: '8px' }}
              >
                You need to contact with Leapeasy suppor team{' '}
                <a href={'mailto:claims@leapeasy.com'}>claims@leapeasy.com</a>.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button variant="primary" onClick={() => setIsValidPolicy(4)}>
                Ok
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {isValidPolicy === 2 && (
          <Dialog open={isValidPolicy === 2} maxWidth="md">
            <DialogContent>
              <Typography
                variant="body1"
                style={{ fontWeight: '600', textAlign: 'center', color: colors.purple[900] }}
              >
                This policy's Coverage Duration in Months equals 0.
              </Typography>
              <Typography
                variant="body2"
                style={{ textAlign: 'center', color: colors.neutral[900], marginTop: '8px' }}
              >
                If you have any questions, please contact{' '}
                <a href={'mailto:claims@leapeasy.com'}>claims@leapeasy.com</a>
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button variant="primary" onClick={() => setIsValidPolicy(4)}>
                Ok
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {isValidPolicy === 3 && (
          <Dialog open={isValidPolicy === 3} maxWidth="md">
            <DialogContent>
              <Typography
                variant="body1"
                style={{ fontWeight: '600', textAlign: 'center', color: colors.purple[900] }}
              >
                The current date is greater than 120 days after the policy expiration date
              </Typography>
              <Typography
                variant="body2"
                style={{ textAlign: 'center', color: colors.neutral[900], marginTop: '8px' }}
              >
                If you have any questions, please contact{' '}
                <a href={'mailto:claims@leapeasy.com'}>claims@leapeasy.com</a>
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button variant="primary" onClick={() => setIsValidPolicy(4)}>
                Ok
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>

      <Dialog open={leaseDateError} maxWidth="xs">
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '24px 20px',
          }}
        >
          <FileSearchIcon />

          <Typography
            variant="body1"
            style={{ fontWeight: '600', textAlign: 'center', color: colors.purple[900] }}
          >
            Please confirm that entered lease term dates are accurate.
          </Typography>

          <Typography
            variant="body2"
            style={{ textAlign: 'center', color: colors.neutral[900], marginTop: '8px' }}
          >
            The lease start and end date you have entered is outside the selected policy’s term.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ padding: '24px 20px', paddingTop: 0 }}>
          <Button
            size="medium"
            variant="tertiary"
            onClick={() => handleLeaseTermConfirm(false)}
            style={{ flexShrink: 2 }}
          >
            No
          </Button>

          <Button
            size="medium"
            variant="primary"
            onClick={() => handleLeaseTermConfirm(true)}
            style={{ flexShrink: 2 }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {duplicated && (
        <Dialog
          open={!!duplicated}
          maxWidth="md"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '24px 20px',
          }}
        >
          <DialogContent sx={{ textAlign: 'center' }}>
            <Typography
              variant="body1"
              style={{ fontWeight: '600', textAlign: 'center', color: colors.purple[900] }}
            >
              Oops!
            </Typography>
            <Typography
              variant="body1"
              style={{ fontWeight: '600', textAlign: 'center', color: colors.purple[900] }}
            >
              It looks like a claim has already been filed on this policy.
            </Typography>
            <Typography
              variant="body2"
              style={{ textAlign: 'center', color: colors.neutral[900], marginTop: '8px' }}
            >
              Please contact:{' '}
              <a href={'mailto:claims@leapeasy.com?subject=' + riderName}>claims@leapeasy.com</a>
            </Typography>
            <Typography
              variant="body2"
              style={{ textAlign: 'center', color: colors.neutral[900], marginTop: '8px' }}
            >
              RiderID: {riderName}
            </Typography>
            <Typography
              variant="body2"
              style={{ textAlign: 'center', color: colors.neutral[900], marginTop: '8px' }}
            >
              Claim # {duplicated.Name}
            </Typography>
            <Typography
              variant="body2"
              style={{ textAlign: 'center', color: colors.neutral[900], marginTop: '8px' }}
            >
              Created Date {new Date(duplicated.CreatedDate).toISOString().slice(0, 10)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="primary" onClick={() => setDuplicated(null)}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', py: 2 }}>
        <Button
          onClick={() => handleNextButton()}
          variant="primary"
          size="medium"
          disabled={
            !(state && landlord && apartment && policy && leaseStartDate && leaseEndDate) ||
            (policy && !formData.policyDuplicated) ||
            (formData.policyDuplicated?.length ?? 0) > 0 ||
            new Date(policyStartDate) > new Date() ||
            isValidPolicy !== 0
          }
        >
          <Typography variant="body1">Next</Typography>
        </Button>
      </Box>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    formData: selectClaimFormState(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators(
      {
        setDataAction,
        getLandlordAction,
        getApartmentAction,
        getRiderAction,
        getTenantsAction,
        setLandlordArrayAction,
        setApartmentArrayAction,
        setRiderArrayAction,
        checkPolicyDuplicatedAction,
        setPolicyDuplicatedAction,
      },
      dispatch,
    ),
  };
};

const ConnectedForm1Dropdown = connect(mapStateToProps, mapDispatchToProps)(Form1Dropdown);

const Form1 = (props) => {
  const { isPortalPrefill, ...rest } = props;

  if (isPolicyNumberEntryEnabled() && !isPortalPrefill) {
    return <Form1PolicyLookup {...rest} />;
  }
  return <ConnectedForm1Dropdown {...rest} isPortalPrefill={isPortalPrefill} />;
};

export default Form1;
